import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Alert, Col, ColorPicker, Input, Row, Tag, Tooltip } from 'antd'
import {
    AimOutlined,
    BgColorsOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CreditCardOutlined,
    FormOutlined,
    RollbackOutlined,
    MobileOutlined,
    SafetyCertificateOutlined,
    ShoppingOutlined,
    StarOutlined,
    StopOutlined,
} from '@ant-design/icons'
import controller, { ROLE_COLORS, StatusRole, cancelUnavailable, captureUnavailable, statusRole } from './controller'
import { STATUS_ICONS } from './OrderStatusIcons'
import { RoleTag, StatusGlyph, hexToRgba, statusColor } from './OrderStatuses'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { PlainField, SectionBadge, ToggleField } from '../../../components/SectionCard'
import { REVIEW_PLANS } from '../../settings/reviews/reviewPlans'
import './order_status.less'

const ROLES: StatusRole[] = ['none', 'initial', 'completed', 'canceled']

const COLOR_PRESETS = [
    '7264D6', '1677FF', '13C2C2', '52C41A', 'A0D911',
    'FAAD14', 'FA8C16', 'F5222D', 'EB2F96', '8C8C8C', '3F3D56',
]

const Section = ({ icon, title, background, extra, children }: any) => (
    <div className="order__block" style={{ width: '100%', height: 'auto' }}>
        <SectionBadge icon={icon} text={title} background={background} toggle={extra} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            {children}
        </div>
    </div>
)

const RoleSection = observer(({ savedRole }: { savedRole: StatusRole }) => {
    const role = statusRole(controller.item)
    const owner = controller.roleOwner(role)

    return (
        <Section
            icon={<SafetyCertificateOutlined />}
            title={i.t('orderStatus:sections.role')}
            background="linear-gradient(135deg, #34526e, #567f9e)"
        >
            <div className="os-role-options" role="radiogroup">
                {ROLES.map(option => (
                    <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={role === option}
                        className={`os-role-option${role === option ? ' is-selected' : ''}`}
                        onClick={() => controller.setRole(option)}
                    >
                        <div className="os-role-option__title">
                            <span className="os-role-option__dot" />
                            {option === 'none'
                                ? i.t(`orderStatus:roles.${option}`)
                                : <Tag color={ROLE_COLORS[option]} style={{ margin: 0 }}>{i.t(`orderStatus:roles.${option}`)}</Tag>
                            }
                        </div>
                        <div className="os-role-option__text">{i.t(`orderStatus:roleHints.${option}`)}</div>
                    </button>
                ))}
            </div>

            {owner && (
                <Alert
                    type="warning"
                    showIcon
                    message={i.t('orderStatus:roleMove', { name: owner.name })}
                />
            )}

            {controller.item.id > 0 && savedRole !== 'none' && (
                <div className="os-hint">{i.t('orderStatus:roleLocked')}</div>
            )}
        </Section>
    )
})

const AppSection = observer(() => {
    const unavailable = cancelUnavailable(statusRole(controller.item))

    return (
        <Section
            icon={<MobileOutlined />}
            title={i.t('orderStatus:sections.app')}
            background="linear-gradient(135deg, #8a5310, #c08234)"
        >
            <ToggleField
                icon={<CloseCircleOutlined />}
                label={i.t('orderStatus:fields.allowCancel')}
                checked={controller.item.allow_cancel}
                onChange={value => controller.setAllowCancel(value)}
                disabled={unavailable}
            />
            <div className="os-hint" style={{ marginTop: -6 }}>
                {i.t(unavailable ? 'orderStatus:hints.allowCancelUnavailable' : 'orderStatus:hints.allowCancel')}
            </div>

            {controller.item.allow_cancel && (
                <>
                    <ToggleField
                        icon={<RollbackOutlined />}
                        label={i.t('orderStatus:fields.refundOnCancel')}
                        checked={controller.item.refund_on_cancel}
                        onChange={value => controller.set('refund_on_cancel', value)}
                    />
                    <div className="os-hint" style={{ marginTop: -6 }}>
                        {i.t('orderStatus:hints.refundOnCancel')}
                    </div>
                </>
            )}

            {controller.item.refund_on_cancel && controller.item.capture_payment && (
                <Alert type="warning" showIcon message={i.t('orderStatus:hints.captureVsRefund')} />
            )}
        </Section>
    )
})

const PaymentSection = observer(() => {
    const unavailable = captureUnavailable(statusRole(controller.item))

    return (
        <Section
            icon={<CreditCardOutlined />}
            title={i.t('orderStatus:sections.payment')}
            background="linear-gradient(135deg, #1f5f8b, #3f8fc4)"
        >
            <ToggleField
                icon={<CheckCircleOutlined />}
                label={i.t('orderStatus:fields.capturePayment')}
                checked={controller.item.capture_payment}
                onChange={value => controller.set('capture_payment', value)}
                disabled={unavailable}
            />
            <div className="os-hint" style={{ marginTop: -6 }}>
                {i.t(unavailable ? 'orderStatus:hints.capturePaymentUnavailable' : 'orderStatus:hints.capturePayment')}
            </div>
        </Section>
    )
})

const ReviewsSection = observer(() => (
    <Section
        icon={<StarOutlined />}
        title={i.t('orderStatus:sections.reviews')}
        background="linear-gradient(135deg, #7a3e6c, #a8619a)"
    >
        <ToggleField
            icon={<StarOutlined />}
            label={i.t('orderStatus:fields.reviewAvailable')}
            checked={controller.item.review_available}
            onChange={value => controller.set('review_available', value)}
            plan={REVIEW_PLANS.order}
        />
        <div className="os-hint" style={{ marginTop: -6 }}>{i.t('orderStatus:hints.reviewAvailable')}</div>

        <ToggleField
            icon={<ShoppingOutlined />}
            label={i.t('orderStatus:fields.purchaseConfirmed')}
            checked={controller.item.purchase_confirmed}
            onChange={value => controller.set('purchase_confirmed', value)}
            plan={REVIEW_PLANS.product}
        />
        <div className="os-hint" style={{ marginTop: -6 }}>{i.t('orderStatus:hints.purchaseConfirmed')}</div>
    </Section>
))

const AppearanceSection = observer(() => {
    const item = controller.item
    const color = statusColor(item)

    return (
        <Section
            icon={<BgColorsOutlined />}
            title={i.t('orderStatus:sections.appearance')}
            background="linear-gradient(135deg, var(--primary-color), #9186e0)"
        >
            <div className="os-preview">
                <div className="os-preview__row">
                    <span className="os-preview__label">{i.t('orderStatus:preview.orders')}</span>
                    <Tag color={`#${color}`} style={{ margin: 0 }}>
                        {item.name || i.t('orderStatus:titleNew')}
                    </Tag>
                </div>
                <div className="os-preview__row">
                    <span className="os-preview__label">{i.t('orderStatus:preview.app')}</span>
                    <span className="os-chip" style={{ background: hexToRgba(color, 0.12), color: `#${color}` }}>
                        <span className="os-chip__icon">
                            <StatusGlyph status={item} />
                        </span>
                        <span className="os-chip__name">{item.name || i.t('orderStatus:titleNew')}</span>
                    </span>
                </div>
            </div>

            <PlainField label={i.t('orderStatus:fields.color')}>
                <div className="os-swatches">
                    {COLOR_PRESETS.map(preset => {
                        const selected = preset.toUpperCase() === color.toUpperCase()

                        return (
                            <button
                                key={preset}
                                type="button"
                                className="os-swatch"
                                aria-label={`#${preset}`}
                                style={{
                                    background: `#${preset}`,
                                    boxShadow: selected ? `0 0 0 2px #${preset}` : undefined,
                                }}
                                onClick={() => controller.set('color_hex', preset)}
                            />
                        )
                    })}
                    <ColorPicker
                        disabledAlpha
                        showText
                        value={`#${color}`}
                        onChange={value => controller.set('color_hex', value.toHex().toUpperCase())}
                    />
                </div>
            </PlainField>

            <PlainField label={i.t('orderStatus:fields.icon')}>
                <div className="os-icon-grid">
                    <Tooltip title={i.t('orderStatus:noIcon')}>
                        <button
                            type="button"
                            className="os-icon-option"
                            aria-label={i.t('orderStatus:noIcon')}
                            style={!item.icon ? { borderColor: `#${color}`, background: hexToRgba(color, 0.08) } : undefined}
                            onClick={() => controller.set('icon', '')}
                        >
                            <StopOutlined />
                        </button>
                    </Tooltip>
                    {STATUS_ICONS.map(({ key, Icon }) => {
                        const selected = item.icon === key

                        return (
                            <button
                                key={key}
                                type="button"
                                className="os-icon-option"
                                aria-label={key}
                                style={selected ? { borderColor: `#${color}`, background: hexToRgba(color, 0.08) } : undefined}
                                onClick={() => controller.set('icon', key)}
                            >
                                <Icon color={color} />
                            </button>
                        )
                    })}
                </div>
            </PlainField>
        </Section>
    )
})

export const OrderStatusPage = observer(() => {
    // @ts-ignore
    const { statusID } = useParams()
    const history = useHistory()
    const id = Number(statusID) || 0
    const isNew = id === 0

    useEffect(() => {
        controller.getOne(id)
        // Список нужен, чтобы знать, у какого статуса сейчас каждая роль
        controller.getList()
    }, [id])

    const item = controller.item
    // Удаление решает сохранённое состояние, а не то, что сейчас выбрано в форме
    const saved = controller.list.find((el: any) => el.id === id)
    const savedRole = statusRole(saved)

    const title = isNew
        ? i.t('orderStatus:titleNew')
        : (saved?.name || `${i.t('orderStatus:titleDetail')} #${id}`)

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        controller.save()
            .then(result => {
                if (isNew && result?.id) history.replace(`/shop/statuses/${result.id}`)
            })
            .catch(() => {})
    }

    const onRemove = () => {
        controller.remove(id).then(removed => {
            if (removed) history.push('/shop/statuses')
        })
    }

    const buttons = [
        { title: i.t('def:actions.backList'), click: () => history.push('/shop/statuses'), type: 'dashed' },
        ...(isNew ? [] : [{
            title: i.t('def:actions.remove'),
            click: onRemove,
            type: 'dashed',
            danger: true,
            disabled: savedRole !== 'none',
        }]),
        { title: i.t('def:actions.save'), htmlType: 'submit', type: 'primary' },
    ]

    return (
        <form onSubmit={onSubmit}>
            <Titles
                title={title}
                buttons={buttons}
                breadcrumb={[
                    { title: i.t('orderStatus:title'), href: '/shop/statuses' },
                    { title },
                ]}
            />

            {controller.itemLoaded && (
                <Row gutter={[20, 20]}>
                    <Col span={24} xl={14}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <Section
                                icon={<FormOutlined />}
                                title={i.t('orderStatus:sections.main')}
                                background="linear-gradient(135deg, #3f3d56, #63607f)"
                                extra={!isNew && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {savedRole !== 'none' && <RoleTag role={savedRole} />}
                                        <span style={{ color: 'rgba(255,255,255,.7)', fontSize: 13 }}>ID {id}</span>
                                    </span>
                                )}
                            >
                                <PlainField label={i.t('orderStatus:fields.name')}>
                                    <Input
                                        size="large"
                                        required
                                        value={item.name}
                                        placeholder={i.t('orderStatus:placeholders.name')}
                                        onChange={e => controller.set('name', e.target.value)}
                                    />
                                </PlainField>

                                <PlainField label={i.t('orderStatus:fields.idCrm')}>
                                    <Input
                                        value={item.id_crm}
                                        placeholder={i.t('orderStatus:placeholders.idCrm')}
                                        onChange={e => controller.set('id_crm', e.target.value)}
                                    />
                                    <div className="os-hint" style={{ marginTop: 6 }}>{i.t('orderStatus:hints.idCrm')}</div>
                                </PlainField>
                            </Section>

                            <RoleSection savedRole={savedRole} />

                            <AppSection />

                            <PaymentSection />

                            <ReviewsSection />

                            <Section
                                icon={<AimOutlined />}
                                title={i.t('orderStatus:sections.buttons')}
                                background="linear-gradient(135deg, #2f6f5e, #4c9b85)"
                            >
                                <div className="os-hint" style={{ marginTop: -4 }}>{i.t('orderStatus:hints.buttons')}</div>
                                <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                    <Col span={24} md={12}>
                                        <PlainField label={i.t('orderStatus:fields.buttonEnter')}>
                                            <Input
                                                value={item.name_button_enter}
                                                placeholder={i.t('orderStatus:placeholders.buttonEnter')}
                                                onChange={e => controller.set('name_button_enter', e.target.value)}
                                            />
                                        </PlainField>
                                    </Col>
                                    <Col span={24} md={12}>
                                        <PlainField label={i.t('orderStatus:fields.buttonComplete')}>
                                            <Input
                                                value={item.name_button_complete}
                                                placeholder={i.t('orderStatus:placeholders.buttonComplete')}
                                                onChange={e => controller.set('name_button_complete', e.target.value)}
                                            />
                                        </PlainField>
                                    </Col>
                                </Row>
                            </Section>
                        </div>
                    </Col>

                    <Col span={24} xl={10}>
                        <AppearanceSection />
                    </Col>
                </Row>
            )}
        </form>
    )
})
